import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, Package as PackageIcon, CreditCard, Palette, Plus, Trash2, Pencil, Loader2, IndianRupee, Activity, Ticket, Plug, KeyRound, CheckCircle2, XCircle, Copy, Mail, Send } from "lucide-react";
import { toast } from "sonner";

const emptyPkg = { name: "", description: "", price: 99900, max_migrations: 10, storage_per_mailbox_gb: 50, storage_mb: null, max_mailboxes: -1, validity_days: 30, is_active: true, sort_order: 0 };

/** Given a package's storage fields, return { value, unit } for display in an editable form. */
function storageToForm(pkg) {
  if (pkg?.storage_mb != null) {
    if (pkg.storage_mb % 1024 === 0) return { value: pkg.storage_mb / 1024, unit: "GB" };
    return { value: pkg.storage_mb, unit: "MB" };
  }
  return { value: pkg?.storage_per_mailbox_gb ?? 50, unit: "GB" };
}

/** Format storage for display — shows MB for small trial plans, GB otherwise. */
function formatStorage(pkg) {
  const mb = pkg?.storage_mb != null ? pkg.storage_mb : (pkg?.storage_per_mailbox_gb ?? 0) * 1024;
  return mb >= 1024 ? `${(mb / 1024).toFixed(mb % 1024 === 0 ? 0 : 1)}GB` : `${mb}MB`;
}

function CouponDialog({ onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", percent_off: 10, active: true, max_redemptions: "", expires_at: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      await api.post("/admin/coupons", {
        code: form.code, percent_off: Number(form.percent_off), active: form.active,
        max_redemptions: form.max_redemptions ? Number(form.max_redemptions) : null,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
      });
      toast.success("Coupon created");
      setOpen(false); setForm({ code: "", percent_off: 10, active: true, max_redemptions: "", expires_at: "" }); onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="gap-2" data-testid="add-coupon-button"><Plus className="h-4 w-4" /> New Coupon</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>New Coupon</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1"><Label>Code</Label><Input value={form.code} onChange={(e) => set("code", e.target.value.toUpperCase())} placeholder="SAVE20" className="font-mono" data-testid="coupon-code-input" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1"><Label>% off</Label><Input type="number" min={1} max={100} value={form.percent_off} onChange={(e) => set("percent_off", e.target.value)} data-testid="coupon-percent-input" /></div>
            <div className="space-y-1"><Label>Max uses (blank=∞)</Label><Input type="number" value={form.max_redemptions} onChange={(e) => set("max_redemptions", e.target.value)} /></div>
          </div>
          <div className="space-y-1"><Label>Expires (optional)</Label><Input type="datetime-local" value={form.expires_at} onChange={(e) => set("expires_at", e.target.value)} /></div>
        </div>
        <DialogFooter><Button onClick={save} disabled={busy || !form.code} data-testid="save-coupon-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmailTemplateDialog({ tmpl, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(tmpl);
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/email-templates/${tmpl.key}`, {
        subject: form.subject, body_text: form.body_text, body_html: form.body_html || null, is_active: form.is_active,
      });
      toast.success("Template saved");
      setOpen(false);
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  const sendTest = async () => {
    if (!testTo) { toast.error("Enter an email address"); return; }
    setTesting(true);
    try {
      await api.post(`/admin/email-templates/${tmpl.key}/test`, { to: testTo });
      toast.success(`Test sent to ${testTo} (with sample data)`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setTesting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setForm(tmpl); }}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" data-testid={`edit-template-${tmpl.key}`}><Pencil className="h-4 w-4" /></Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader><DialogTitle>{tmpl.name}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">{tmpl.description}</p>
        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-md border border-border px-4 py-3">
            <span className="text-sm">Active (send this email)</span>
            <Switch checked={form.is_active} onCheckedChange={(v) => set("is_active", v)} data-testid={`template-active-${tmpl.key}`} />
          </div>
          <div className="space-y-1"><Label>Subject</Label>
            <Input value={form.subject} onChange={(e) => set("subject", e.target.value)} className="font-mono text-xs" data-testid={`template-subject-${tmpl.key}`} />
          </div>
          <div className="space-y-1"><Label>Body (plain text — used as the email fallback)</Label>
            <Textarea rows={6} value={form.body_text} onChange={(e) => set("body_text", e.target.value)} className="font-mono text-xs" data-testid={`template-body-${tmpl.key}`} />
          </div>
          <div className="space-y-1"><Label>Body (HTML — the styled version most inboxes show)</Label>
            <Textarea rows={8} value={form.body_html || ""} onChange={(e) => set("body_html", e.target.value)} className="font-mono text-xs" data-testid={`template-body-html-${tmpl.key}`} />
            <p className="text-[10px] text-muted-foreground">Leave blank to send plain text only. Uses {"{{brand_color}}"} for your theme color.</p>
          </div>
          <p className="text-[10px] text-muted-foreground">
            Available variables: <code className="font-mono">{"{{name}} {{email}} {{site_name}} {{app_url}}"}</code> plus event-specific ones listed above.
          </p>
        </div>
        <DialogFooter className="flex-col gap-3 sm:flex-col sm:items-stretch">
          <div className="flex items-end gap-2">
            <div className="flex-1 space-y-1"><Label className="text-xs">Send test (with sample data)</Label>
              <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@example.com" data-testid={`template-test-to-${tmpl.key}`} />
            </div>
            <Button variant="outline" onClick={sendTest} disabled={testing} className="gap-2" data-testid={`template-send-test-${tmpl.key}`}>
              {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Test
            </Button>
          </div>
          <Button onClick={save} disabled={busy} className="gap-2" data-testid={`save-template-${tmpl.key}`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmailTemplatesPanel() {
  const [templates, setTemplates] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/email-templates");
    setTemplates(data);
  };
  useEffect(() => { load(); }, []);

  if (!templates) return <div className="py-10 text-center text-muted-foreground"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>;

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        In emails ko customize karo — {"{{variable}}"} placeholders se personalize hoti hain. Off karne se wo email bhejni band ho jaati hai.
      </p>
      {templates.map((t) => (
        <div key={t.key} className="flex items-center justify-between border border-border bg-card p-4" data-testid={`template-row-${t.key}`}>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium">{t.name}</span>
              {t.is_active
                ? <span className="text-xs font-medium text-emerald-600">Active</span>
                : <span className="text-xs font-medium text-muted-foreground">Off</span>}
            </div>
            <p className="truncate text-xs text-muted-foreground">{t.description}</p>
          </div>
          <EmailTemplateDialog tmpl={t} onSaved={load} />
        </div>
      ))}
    </div>
  );
}

function StatusChip({ ok }) {
  return ok ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700"><CheckCircle2 className="h-3 w-3" /> Configured</span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700"><XCircle className="h-3 w-3" /> Not set</span>
  );
}

function IntegrationsPanel() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = async () => {
    const { data } = await api.get("/admin/integrations");
    setData(data);
    setForm({
      GOOGLE_CLIENT_ID: data.values.GOOGLE_CLIENT_ID || "",
      MS_CLIENT_ID: data.values.MS_CLIENT_ID || "",
      MS_TENANT: data.values.MS_TENANT || "",
      OAUTH_PUBLIC_BASE: data.values.OAUTH_PUBLIC_BASE || "",
      RAZORPAY_KEY_ID: data.values.RAZORPAY_KEY_ID || "",
      SMTP_HOST: data.values.SMTP_HOST || "",
      SMTP_PORT: data.values.SMTP_PORT || "587",
      SMTP_USER: data.values.SMTP_USER || "",
      SMTP_FROM: data.values.SMTP_FROM || "",
      SMTP_FROM_NAME: data.values.SMTP_FROM_NAME || "",
      GOOGLE_CLIENT_SECRET: "", MS_CLIENT_SECRET: "", RAZORPAY_KEY_SECRET: "", RAZORPAY_WEBHOOK_SECRET: "", SMTP_PASSWORD: "",
    });
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      // omit empty secret fields so existing secrets are preserved
      const payload = {};
      Object.entries(form).forEach(([k, v]) => {
        const isSecret = ["GOOGLE_CLIENT_SECRET", "MS_CLIENT_SECRET", "RAZORPAY_KEY_SECRET", "RAZORPAY_WEBHOOK_SECRET", "SMTP_PASSWORD"].includes(k);
        if (isSecret && !v) return;
        payload[k] = v;
      });
      const { data } = await api.put("/admin/integrations", payload);
      setData(data);
      setForm((f) => ({ ...f, GOOGLE_CLIENT_SECRET: "", MS_CLIENT_SECRET: "", RAZORPAY_KEY_SECRET: "", RAZORPAY_WEBHOOK_SECRET: "", SMTP_PASSWORD: "" }));
      toast.success("Integration keys saved");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const sendTest = async () => {
    if (!testTo) { toast.error("Enter an email address to send the test to"); return; }
    setTesting(true);
    try {
      await api.post("/admin/test-email", { to: testTo });
      toast.success(`Test email sent to ${testTo}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setTesting(false); }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };
  if (!data) return <div className="py-10 text-center text-muted-foreground"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>;
  const secretPh = (k) => (data.secrets_set[k] ? "•••••••• saved — leave blank to keep" : "Not set");

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Yahin se OAuth aur Razorpay keys manage karein — server ki <code className="font-mono">.env</code> file ko haath lagaye bina.
        Keys database mein encrypted store hoti hain aur turant apply ho jaati hain.
      </p>

      {/* Google */}
      <div className="border border-border bg-card p-6" data-testid="integ-google">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2"><Plug className="h-4 w-4 text-primary" /><span className="font-heading font-bold tracking-tight">Google (Drive & Contacts)</span></div>
          <StatusChip ok={data.status.google} />
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1"><Label>Client ID</Label><Input value={form.GOOGLE_CLIENT_ID} onChange={(e) => set("GOOGLE_CLIENT_ID", e.target.value)} className="font-mono text-xs" data-testid="integ-google-id" /></div>
          <div className="space-y-1"><Label>Client Secret</Label><Input type="password" value={form.GOOGLE_CLIENT_SECRET} onChange={(e) => set("GOOGLE_CLIENT_SECRET", e.target.value)} placeholder={secretPh("GOOGLE_CLIENT_SECRET")} className="font-mono text-xs" data-testid="integ-google-secret" /></div>
        </div>
        <div className="mt-3 flex items-center gap-2 rounded-md bg-muted px-3 py-2">
          <span className="text-xs text-muted-foreground">Redirect URI:</span>
          <code className="flex-1 truncate font-mono text-xs">{data.redirect_uris.google}</code>
          <Button variant="ghost" size="icon" onClick={() => copy(data.redirect_uris.google)} data-testid="copy-redirect-google"><Copy className="h-3.5 w-3.5" /></Button>
        </div>
      </div>

      {/* Microsoft */}
      <div className="border border-border bg-card p-6" data-testid="integ-microsoft">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2"><Plug className="h-4 w-4 text-primary" /><span className="font-heading font-bold tracking-tight">Microsoft (OneDrive & Contacts)</span></div>
          <StatusChip ok={data.status.microsoft} />
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1"><Label>Client ID</Label><Input value={form.MS_CLIENT_ID} onChange={(e) => set("MS_CLIENT_ID", e.target.value)} className="font-mono text-xs" data-testid="integ-ms-id" /></div>
          <div className="space-y-1"><Label>Client Secret</Label><Input type="password" value={form.MS_CLIENT_SECRET} onChange={(e) => set("MS_CLIENT_SECRET", e.target.value)} placeholder={secretPh("MS_CLIENT_SECRET")} className="font-mono text-xs" data-testid="integ-ms-secret" /></div>
          <div className="space-y-1"><Label>Tenant (default: common)</Label><Input value={form.MS_TENANT} onChange={(e) => set("MS_TENANT", e.target.value)} placeholder="common" className="font-mono text-xs" data-testid="integ-ms-tenant" /></div>
        </div>
        <div className="mt-3 flex items-center gap-2 rounded-md bg-muted px-3 py-2">
          <span className="text-xs text-muted-foreground">Redirect URI:</span>
          <code className="flex-1 truncate font-mono text-xs">{data.redirect_uris.microsoft}</code>
          <Button variant="ghost" size="icon" onClick={() => copy(data.redirect_uris.microsoft)} data-testid="copy-redirect-microsoft"><Copy className="h-3.5 w-3.5" /></Button>
        </div>
      </div>

      {/* Razorpay */}
      <div className="border border-border bg-card p-6" data-testid="integ-razorpay">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2"><KeyRound className="h-4 w-4 text-primary" /><span className="font-heading font-bold tracking-tight">Razorpay (Payments)</span></div>
          <StatusChip ok={data.status.razorpay} />
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1"><Label>Key ID</Label><Input value={form.RAZORPAY_KEY_ID} onChange={(e) => set("RAZORPAY_KEY_ID", e.target.value)} placeholder="rzp_live_..." className="font-mono text-xs" data-testid="integ-rzp-id" /></div>
          <div className="space-y-1"><Label>Key Secret</Label><Input type="password" value={form.RAZORPAY_KEY_SECRET} onChange={(e) => set("RAZORPAY_KEY_SECRET", e.target.value)} placeholder={secretPh("RAZORPAY_KEY_SECRET")} className="font-mono text-xs" data-testid="integ-rzp-secret" /></div>
          <div className="space-y-1 md:col-span-2"><Label>Webhook Secret</Label><Input type="password" value={form.RAZORPAY_WEBHOOK_SECRET} onChange={(e) => set("RAZORPAY_WEBHOOK_SECRET", e.target.value)} placeholder={secretPh("RAZORPAY_WEBHOOK_SECRET")} className="font-mono text-xs" data-testid="integ-rzp-webhook" /></div>
        </div>
        {!data.secrets_set.RAZORPAY_WEBHOOK_SECRET && (
          <p className="mt-3 text-xs text-amber-600" data-testid="rzp-webhook-warning">
            ⚠ Webhook secret set nahi hai — auto payment confirmation (webhook) kaam nahi karega jab tak ye set nahi hota.
          </p>
        )}
        {data.webhook_url && (
          <div className="mt-3 flex items-center gap-2 rounded-md bg-muted px-3 py-2">
            <span className="text-xs text-muted-foreground">Webhook URL:</span>
            <code className="flex-1 truncate font-mono text-xs">{data.webhook_url}</code>
            <Button variant="ghost" size="icon" onClick={() => copy(data.webhook_url)} data-testid="copy-webhook-url"><Copy className="h-3.5 w-3.5" /></Button>
          </div>
        )}
      </div>

      {/* SMTP */}
      <div className="border border-border bg-card p-6" data-testid="integ-smtp">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-primary" /><span className="font-heading font-bold tracking-tight">SMTP (Outgoing Email)</span></div>
          <StatusChip ok={data.status.smtp} />
        </div>
        <p className="mb-4 text-xs text-muted-foreground">Server change hone par yahin se SMTP update kar sakte hain — welcome, password reset, aur migration-status emails isी se bhejte hain.</p>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1"><Label>SMTP Host</Label><Input value={form.SMTP_HOST} onChange={(e) => set("SMTP_HOST", e.target.value)} placeholder="smtp.yourprovider.com" className="font-mono text-xs" data-testid="smtp-host" /></div>
          <div className="space-y-1"><Label>SMTP Port</Label><Input value={form.SMTP_PORT} onChange={(e) => set("SMTP_PORT", e.target.value)} placeholder="587" className="font-mono text-xs" data-testid="smtp-port" /></div>
          <div className="space-y-1"><Label>SMTP Username</Label><Input value={form.SMTP_USER} onChange={(e) => set("SMTP_USER", e.target.value)} className="font-mono text-xs" data-testid="smtp-user" /></div>
          <div className="space-y-1"><Label>SMTP Password</Label><Input type="password" value={form.SMTP_PASSWORD} onChange={(e) => set("SMTP_PASSWORD", e.target.value)} placeholder={secretPh("SMTP_PASSWORD")} className="font-mono text-xs" data-testid="smtp-password" /></div>
          <div className="space-y-1"><Label>From Address</Label><Input value={form.SMTP_FROM} onChange={(e) => set("SMTP_FROM", e.target.value)} placeholder="no-reply@yourdomain.com" className="font-mono text-xs" data-testid="smtp-from" /></div>
          <div className="space-y-1"><Label>From Name</Label><Input value={form.SMTP_FROM_NAME} onChange={(e) => set("SMTP_FROM_NAME", e.target.value)} placeholder="MailShift" className="font-mono text-xs" data-testid="smtp-from-name" /></div>
        </div>
        <div className="mt-4 flex items-end gap-2">
          <div className="flex-1 space-y-1"><Label>Send a test email to</Label><Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@example.com" data-testid="smtp-test-to" /></div>
          <Button variant="outline" onClick={sendTest} disabled={testing} className="gap-2" data-testid="smtp-send-test">
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Send test
          </Button>
        </div>
      </div>

      {/* OAuth base */}
      <div className="border border-border bg-card p-6">
        <div className="space-y-1"><Label>Public Base URL (for OAuth redirects)</Label>
          <Input value={form.OAUTH_PUBLIC_BASE} onChange={(e) => set("OAUTH_PUBLIC_BASE", e.target.value)} placeholder="https://yourdomain.com" className="font-mono text-xs" data-testid="integ-oauth-base" />
          <p className="text-xs text-muted-foreground">Aapke live domain ka base URL. Redirect URIs isi se banti hain — same URI Google/Microsoft app mein register karein.</p>
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={busy} className="gap-2" data-testid="save-integrations-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Save keys
        </Button>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="border border-border bg-card p-6">
      <div className="flex items-center justify-between text-muted-foreground">
        <span className="text-xs uppercase tracking-[0.2em]">{label}</span><Icon className="h-4 w-4" />
      </div>
      <p className="mt-4 font-heading text-3xl font-bold tracking-tighter">{value}</p>
    </div>
  );
}

function UserActions({ user, onChanged }) {
  const [open, setOpen] = useState(false);
  const [newPw, setNewPw] = useState("");
  const [busy, setBusy] = useState(false);

  const resetPassword = async () => {
    if (newPw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/users/${user.id}/reset-password`, { new_password: newPw });
      toast.success(`Password reset for ${user.email}`);
      setOpen(false); setNewPw("");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  const toggleActive = async () => {
    const makingActive = user.is_active === false;
    if (!makingActive && !window.confirm(`Disable ${user.email}? They won't be able to log in.`)) return;
    try {
      await api.post(`/admin/users/${user.id}/set-active`, { is_active: makingActive });
      toast.success(makingActive ? "User re-enabled" : "User disabled");
      onChanged();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  return (
    <div className="flex items-center justify-end gap-1">
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button variant="ghost" size="icon" title="Reset password" data-testid={`reset-pw-${user.id}`}><KeyRound className="h-4 w-4" /></Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader><DialogTitle>Reset password for {user.email}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label>New password</Label>
            <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid={`new-pw-input-${user.id}`} />
          </div>
          <DialogFooter>
            <Button onClick={resetPassword} disabled={busy} className="gap-2" data-testid={`confirm-reset-pw-${user.id}`}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Reset password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Button variant="ghost" size="icon"
              title={user.is_active === false ? "Re-enable user" : "Disable user"}
              onClick={toggleActive} data-testid={`toggle-active-${user.id}`}>
        {user.is_active === false ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-red-600" />}
      </Button>
    </div>
  );
}

function PackageDialog({ pkg, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(pkg || emptyPkg);
  const [storage, setStorage] = useState(storageToForm(pkg));
  const [busy, setBusy] = useState(false);
  const editing = !!pkg;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const storageValue = Number(storage.value) || 0;
      const storageMb = storage.unit === "GB" ? storageValue * 1024 : storageValue;
      const payload = { ...form, price: Number(form.price), max_migrations: Number(form.max_migrations),
        storage_per_mailbox_gb: Math.max(1, Math.round(storageMb / 1024)), storage_mb: storageMb,
        max_mailboxes: Number(form.max_mailboxes),
        validity_days: Number(form.validity_days), sort_order: Number(form.sort_order) };
      if (editing) await api.put(`/admin/packages/${pkg.id}`, payload);
      else await api.post("/admin/packages", payload);
      toast.success(editing ? "Package updated" : "Package created");
      setOpen(false); onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) { setForm(pkg || emptyPkg); setStorage(storageToForm(pkg)); } }}>
      <DialogTrigger asChild>
        {editing ? <Button variant="ghost" size="icon" data-testid={`edit-pkg-${pkg.id}`}><Pencil className="h-4 w-4" /></Button>
                 : <Button className="gap-2" data-testid="add-package-button"><Plus className="h-4 w-4" /> New Package</Button>}
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} Package</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2 space-y-1"><Label>Name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="pkg-name" /></div>
          <div className="col-span-2 space-y-1"><Label>Description</Label><Input value={form.description || ""} onChange={(e) => set("description", e.target.value)} /></div>
          <div className="space-y-1"><Label>Price (₹)</Label><Input type="number" value={form.price / 100} onChange={(e) => set("price", Math.round((parseFloat(e.target.value) || 0) * 100))} data-testid="pkg-price" /><p className="text-[10px] text-muted-foreground">Customer pays ₹{(form.price/100).toLocaleString("en-IN")}</p></div>
          <div className="space-y-1"><Label>Validity (days)</Label><Input type="number" value={form.validity_days} onChange={(e) => set("validity_days", e.target.value)} /></div>
          <div className="space-y-1"><Label>Max migrations (-1 = ∞)</Label><Input type="number" value={form.max_migrations} onChange={(e) => set("max_migrations", e.target.value)} /></div>
          <div className="space-y-1"><Label>Max mailboxes (-1 = ∞)</Label><Input type="number" value={form.max_mailboxes} onChange={(e) => set("max_mailboxes", e.target.value)} /></div>
          <div className="space-y-1">
            <Label>Storage/mailbox</Label>
            <div className="flex gap-2">
              <Input type="number" value={storage.value} onChange={(e) => setStorage((s) => ({ ...s, value: e.target.value }))} data-testid="pkg-storage-value" />
              <Select value={storage.unit} onValueChange={(u) => setStorage((s) => ({ ...s, unit: u }))}>
                <SelectTrigger className="w-24" data-testid="pkg-storage-unit"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="MB">MB</SelectItem>
                  <SelectItem value="GB">GB</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-[10px] text-muted-foreground">Use MB for trial plans under 1GB (e.g. 250MB)</p>
          </div>
          <div className="space-y-1"><Label>Sort order</Label><Input type="number" value={form.sort_order} onChange={(e) => set("sort_order", e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={busy || !form.name} data-testid="save-package-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Admin() {
  const { reloadSettings } = useSettings();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [packages, setPackages] = useState([]);
  const [subs, setSubs] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [cms, setCms] = useState(null);
  const [savingCms, setSavingCms] = useState(false);

  const load = async () => {
    const [s, u, p, sub, cp, set] = await Promise.all([
      api.get("/admin/stats"), api.get("/admin/users"), api.get("/admin/packages"),
      api.get("/admin/subscriptions"), api.get("/admin/coupons"), api.get("/settings"),
    ]);
    setStats(s.data); setUsers(u.data); setPackages(p.data); setSubs(sub.data); setCoupons(cp.data); setCms(set.data);
  };
  useEffect(() => { load(); }, []);

  const delPkg = async (id) => {
    try { await api.delete(`/admin/packages/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const delCoupon = async (id) => {
    try { await api.delete(`/admin/coupons/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const saveCms = async () => {
    setSavingCms(true);
    try { await api.put("/admin/settings", cms); toast.success("Site settings saved"); await reloadSettings(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSavingCms(false); }
  };
  const setC = (k, v) => setCms((c) => ({ ...c, [k]: v }));

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Admin</p>
        <h1 className="mt-1 font-heading text-4xl font-bold tracking-tighter">Control Panel</h1>
      </div>

      <div className="mb-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon={Users} label="Users" value={stats?.users ?? "-"} />
        <StatCard icon={Activity} label="Migrations" value={stats?.migrations ?? "-"} />
        <StatCard icon={CreditCard} label="Active Plans" value={stats?.active_subscriptions ?? "-"} />
        <StatCard icon={IndianRupee} label="Revenue" value={stats ? `₹${(stats.revenue_paise / 100).toLocaleString("en-IN")}` : "-"} />
      </div>

      <Tabs defaultValue="packages">
        <TabsList className="mb-6 flex h-auto flex-wrap justify-start gap-1">
          <TabsTrigger value="packages" data-testid="tab-packages"><PackageIcon className="mr-2 h-4 w-4" />Packages</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users"><Users className="mr-2 h-4 w-4" />Users</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments"><CreditCard className="mr-2 h-4 w-4" />Payments</TabsTrigger>
          <TabsTrigger value="coupons" data-testid="tab-coupons"><Ticket className="mr-2 h-4 w-4" />Coupons</TabsTrigger>
          <TabsTrigger value="integrations" data-testid="tab-integrations"><Plug className="mr-2 h-4 w-4" />Integrations</TabsTrigger>
          <TabsTrigger value="emails" data-testid="tab-emails"><Mail className="mr-2 h-4 w-4" />Email Templates</TabsTrigger>
          <TabsTrigger value="cms" data-testid="tab-cms"><Palette className="mr-2 h-4 w-4" />Site Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="packages">
          <div className="mb-4 flex justify-end"><PackageDialog onSaved={load} /></div>
          <div className="border border-border bg-card">
            <Table>
              <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Price</TableHead><TableHead>Migrations</TableHead><TableHead>Mailboxes</TableHead><TableHead>Validity</TableHead><TableHead>Active</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {packages.map((p) => (
                  <TableRow key={p.id} data-testid={`pkg-row-${p.id}`}>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell>₹{(p.price / 100).toLocaleString("en-IN")}</TableCell>
                    <TableCell>{p.max_migrations === -1 ? "∞" : p.max_migrations}</TableCell>
                    <TableCell>{p.max_mailboxes === -1 ? "∞" : p.max_mailboxes}</TableCell>
                    <TableCell>{p.validity_days}d</TableCell>
                    <TableCell>{p.is_active ? "Yes" : "No"}</TableCell>
                    <TableCell className="flex gap-1">
                      <PackageDialog pkg={p} onSaved={load} />
                      <Button variant="ghost" size="icon" onClick={() => delPkg(p.id)} data-testid={`del-pkg-${p.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="users">
          <div className="border border-border bg-card">
            <Table>
              <TableHeader><TableRow><TableHead>Email</TableHead><TableHead>Name</TableHead><TableHead>Role</TableHead><TableHead>Plan</TableHead><TableHead>Migrations</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell className="font-mono text-xs">{u.email}</TableCell>
                    <TableCell>{u.name}</TableCell>
                    <TableCell>{u.role}</TableCell>
                    <TableCell>{u.plan || <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell>{u.migrations}</TableCell>
                    <TableCell>
                      {u.is_active === false
                        ? <span className="text-xs font-medium text-red-600">Disabled</span>
                        : <span className="text-xs font-medium text-emerald-600">Active</span>}
                    </TableCell>
                    <TableCell><UserActions user={u} onChanged={load} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="payments">
          <div className="border border-border bg-card">
            <Table>
              <TableHeader><TableRow><TableHead>User</TableHead><TableHead>Plan</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead><TableHead>Payment ID</TableHead></TableRow></TableHeader>
              <TableBody>
                {subs.length === 0 ? <TableRow><TableCell colSpan={5} className="py-8 text-center text-muted-foreground">No payments yet</TableCell></TableRow> :
                  subs.map((s) => (
                    <TableRow key={s.id} data-testid={`sub-row-${s.id}`}>
                      <TableCell className="font-mono text-xs">{s.user_email}</TableCell>
                      <TableCell>{s.package_name}</TableCell>
                      <TableCell>₹{(s.amount / 100).toLocaleString("en-IN")}</TableCell>
                      <TableCell>{s.status}</TableCell>
                      <TableCell className="font-mono text-xs">{s.razorpay_payment_id || "—"}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="coupons">
          <div className="mb-4 flex justify-end"><CouponDialog onSaved={load} /></div>
          <div className="border border-border bg-card">
            <Table>
              <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>% Off</TableHead><TableHead>Used</TableHead><TableHead>Revenue</TableHead><TableHead>Max</TableHead><TableHead>Active</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {coupons.length === 0 ? <TableRow><TableCell colSpan={7} className="py-8 text-center text-muted-foreground">No coupons yet</TableCell></TableRow> :
                  coupons.map((c) => (
                    <TableRow key={c.id} data-testid={`coupon-row-${c.id}`}>
                      <TableCell className="font-mono font-medium">{c.code}</TableCell>
                      <TableCell>{c.percent_off}%</TableCell>
                      <TableCell>{c.times_used}</TableCell>
                      <TableCell>₹{((c.revenue_paise || 0) / 100).toLocaleString("en-IN")}</TableCell>
                      <TableCell>{c.max_redemptions ?? "∞"}</TableCell>
                      <TableCell>{c.active ? "Yes" : "No"}</TableCell>
                      <TableCell><Button variant="ghost" size="icon" onClick={() => delCoupon(c.id)} data-testid={`del-coupon-${c.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button></TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>


        <TabsContent value="integrations">
          <IntegrationsPanel />
        </TabsContent>

        <TabsContent value="emails">
          <EmailTemplatesPanel />
        </TabsContent>

        <TabsContent value="cms">
          {cms && (
            <div className="max-w-2xl space-y-5 border border-border bg-card p-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1"><Label>Site name</Label><Input value={cms.site_name || ""} onChange={(e) => setC("site_name", e.target.value)} data-testid="cms-site-name" /></div>
                <div className="space-y-1"><Label>Brand color</Label>
                  <div className="mb-2 flex flex-wrap gap-2">
                    {["#2563EB", "#7C3AED", "#DB2777", "#DC2626", "#EA580C", "#16A34A", "#0D9488", "#4F46E5"].map((c) => (
                      <button key={c} type="button" onClick={() => setC("brand_color", c)}
                              className="h-7 w-7 rounded-full ring-offset-2 transition-transform hover:scale-110"
                              style={{ backgroundColor: c, boxShadow: cms.brand_color === c ? `0 0 0 2px ${c}` : "none" }}
                              title={c} data-testid={`theme-swatch-${c}`} />
                    ))}
                  </div>
                  <div className="flex gap-2"><Input type="color" value={cms.brand_color || "#2563EB"} onChange={(e) => setC("brand_color", e.target.value)} className="h-10 w-16 p-1" data-testid="cms-brand-color" />
                    <Input value={cms.brand_color || ""} onChange={(e) => setC("brand_color", e.target.value)} className="font-mono" /></div></div>
              </div>
              <p className="text-[10px] text-muted-foreground">Theme color turant poore panel mein apply ho jaata hai — koi bhi swatch click karo ya custom color choose karo.</p>
              <div className="space-y-1"><Label>Tagline</Label><Input value={cms.tagline || ""} onChange={(e) => setC("tagline", e.target.value)} /></div>
              <div className="space-y-1"><Label>Logo URL</Label><Input value={cms.logo_url || ""} onChange={(e) => setC("logo_url", e.target.value)} placeholder="https://.../logo.png" data-testid="cms-logo-url" /></div>
              <div className="space-y-1"><Label>Hero title</Label><Input value={cms.hero_title || ""} onChange={(e) => setC("hero_title", e.target.value)} /></div>
              <div className="space-y-1"><Label>Hero subtitle</Label><Textarea value={cms.hero_subtitle || ""} onChange={(e) => setC("hero_subtitle", e.target.value)} /></div>
              <div className="space-y-1"><Label>Pricing title</Label><Input value={cms.pricing_title || ""} onChange={(e) => setC("pricing_title", e.target.value)} /></div>
              <div className="space-y-1"><Label>Pricing subtitle</Label><Textarea value={cms.pricing_subtitle || ""} onChange={(e) => setC("pricing_subtitle", e.target.value)} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1"><Label>Contact email</Label><Input value={cms.contact_email || ""} onChange={(e) => setC("contact_email", e.target.value)} /></div>
                <div className="space-y-1"><Label>Footer text</Label><Input value={cms.footer_text || ""} onChange={(e) => setC("footer_text", e.target.value)} /></div>
              </div>
              <Button onClick={saveCms} disabled={savingCms} className="gap-2" data-testid="save-cms-button">
                {savingCms ? <Loader2 className="h-4 w-4 animate-spin" /> : <Palette className="h-4 w-4" />} Save settings
              </Button>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
